import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, Phone, Mail, FileText, Trophy, Clock, RefreshCw } from 'lucide-react';
import { teamAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

interface TeamMember {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
    primarySkills?: string[];
    secondarySkills?: string[];
    skills?: {
      technical?: string[];
      soft?: string[];
    };
    experience?: {
      yearsOfExperience?: number;
    };
    education?: {
      graduationYear?: number;
    };
    professionalInfo?: {
      yearsOfExperience?: number;
    };
  };
  role: string;
  joinedAt: string;
}

interface ProblemStatement {
  _id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
}

interface HackathonPhase {
  _id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  status: string;
  order: number;
}

interface Hackathon {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  phases: HackathonPhase[];
}

interface Team {
  _id: string;
  name: string;
  hackathon: Hackathon;
  leader: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
    primarySkills?: string[];
    secondarySkills?: string[];
    skills?: {
      technical?: string[];
      soft?: string[];
    };
    experience?: {
      yearsOfExperience?: number;
    };
    education?: {
      graduationYear?: number;
    };
    professionalInfo?: {
      yearsOfExperience?: number;
    };
  };
  members: TeamMember[];
  selectedProblemStatement?: ProblemStatement;
  assignedProblemStatement?: ProblemStatement;
  submission?: {
    title: string;
    description: string;
    githubLink?: string;
    demoLink?: string;
    presentationLink?: string;
    submittedAt: string;
  };
  isApproved: boolean;
  createdAt: string;
}

const MentorTeams: React.FC = () => {
  const { success, error } = useToast();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAssignedTeams();
  }, []);

  const fetchAssignedTeams = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching assigned teams for mentor...');
      const data = await teamAPI.getAssignedTeams();
      console.log('✅ Assigned teams response:', data);
      setTeams(data);
    } catch (err: any) {
      console.error('❌ Error fetching assigned teams:', err);
      error(err.response?.data?.message || 'Failed to fetch assigned teams');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchAssignedTeams();
      success('Teams refreshed successfully');
    } catch (err: any) {
      error('Failed to refresh teams');
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPhaseStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-gray-100 text-gray-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get city from address
  const getCityFromAddress = (address?: string) => {
    if (!address) return '';
    const parts = address.split(',').map(part => part.trim());
    return parts[parts.length - 3] || parts[parts.length - 2] || parts[parts.length - 1] || '';
  };

  // Helper function to format user skills
  const getUserSkills = (user: any) => {
    const skills = [];
    
    // Add primary skills
    if (user.primarySkills && Array.isArray(user.primarySkills) && user.primarySkills.length > 0) {
      skills.push(...user.primarySkills);
    }
    
    // Add secondary skills
    if (user.secondarySkills && Array.isArray(user.secondarySkills) && user.secondarySkills.length > 0) {
      skills.push(...user.secondarySkills);
    }
    
    // Add technical skills from skills object
    if (user.skills?.technical && Array.isArray(user.skills.technical) && user.skills.technical.length > 0) {
      skills.push(...user.skills.technical);
    }
    
    // Add soft skills from skills object
    if (user.skills?.soft && Array.isArray(user.skills.soft) && user.skills.soft.length > 0) {
      skills.push(...user.skills.soft);
    }
    
    // Remove duplicates and limit to 5 skills
    const uniqueSkills = [...new Set(skills)];
    return uniqueSkills.slice(0, 5);
  };

  // Helper function to get user experience
  const getUserExperience = (user: any) => {
    if (user.professionalInfo?.yearsOfExperience) {
      return `${user.professionalInfo.yearsOfExperience} years`;
    }
    if (user.education?.graduationYear) {
      const currentYear = new Date().getFullYear();
      const yearsSinceGraduation = currentYear - user.education.graduationYear;
      if (yearsSinceGraduation >= 0) {
        return yearsSinceGraduation === 0 ? '' : `${yearsSinceGraduation} years`;
      }
    }
    return '';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              My Assigned Teams
            </h1>
            <p className="text-gray-600">
              View and manage teams assigned to you for mentoring
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Teams</p>
                  <p className="text-2xl font-bold text-gray-900">{teams.length}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Teams</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {teams.filter(team => team.isApproved).length}
                  </p>
                </div>
                <Trophy className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">With Submissions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {teams.filter(team => team.submission).length}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Members</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {teams.reduce((total, team) => total + team.members.length, 0)}
                  </p>
                </div>
                <Users className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Teams List */}
      {teams.length === 0 ? (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No teams assigned</h3>
          <p className="text-gray-600">
            You haven't been assigned to any teams yet. Contact an admin to get assigned.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {teams.map((team) => (
            <Card key={team._id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">{team.name}</CardTitle>
                    <p className="text-gray-600 mt-1">
                      Hackathon: {team.hackathon.title}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={team.isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {team.isApproved ? 'Approved' : 'Pending'}
                    </Badge>
                    {team.submission && (
                      <Badge className="bg-blue-100 text-blue-800">
                        Submitted
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="members">Team Members</TabsTrigger>
                    <TabsTrigger value="submission">Submission</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="overview">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Hackathon Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><strong>Title:</strong> {team.hackathon.title}</p>
                            <p><strong>Description:</strong> {team.hackathon.description}</p>
                            <p><strong>Start Date:</strong> {formatDate(team.hackathon.startDate)}</p>
                            <p><strong>End Date:</strong> {formatDate(team.hackathon.endDate)}</p>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Current Phases</h4>
                          <div className="space-y-2">
                            {team.hackathon.phases && team.hackathon.phases.length > 0 ? (
                              team.hackathon.phases
                                .sort((a, b) => a.order - b.order)
                                .map((phase) => (
                                  <div key={phase._id} className="flex items-center justify-between">
                                    <span className="text-sm">{phase.name}</span>
                                    <Badge className={getPhaseStatusColor(phase.status)}>
                                      {phase.status}
                                    </Badge>
                                  </div>
                                ))
                            ) : (
                              <p className="text-sm text-gray-500">No phases defined for this hackathon</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="members">
                    <div className="space-y-4">
                      {/* Team Leader */}
                      <div className="border rounded-lg p-4 bg-blue-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">
                              {team.leader.firstName} {team.leader.lastName}
                            </h4>
                            <Badge className="bg-blue-100 text-blue-800">
                              Team Leader
                            </Badge>
                          </div>
                          <span className="text-sm text-gray-500">
                            Created: {formatDate(team.createdAt)}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span>{team.leader.email}</span>
                          </div>
                          {team.leader.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span>{team.leader.phone}</span>
                            </div>
                          )}
                          {getCityFromAddress(team.leader.address) && (
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>{getCityFromAddress(team.leader.address)}</span>
                            </div>
                          )}
                          {getUserExperience(team.leader) && (
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              <span>Experience: {getUserExperience(team.leader)}</span>
                            </div>
                          )}
                          {getUserSkills(team.leader).length > 0 && (
                            <div className="flex items-start gap-2">
                              <svg className="h-4 w-4 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                              </svg>
                              <div>
                                <span className="font-medium">Skills:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {getUserSkills(team.leader).map((skill, index) => (
                                    <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Team Members */}
                      {team.members
                        .filter(member => member.user._id !== team.leader._id) // Filter out the team leader
                        .map((member) => (
                          <div key={member.user._id} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">
                                  {member.user.firstName} {member.user.lastName}
                                </h4>
                                <Badge className="bg-gray-100 text-gray-800">
                                  {member.role}
                                </Badge>
                              </div>
                              <span className="text-sm text-gray-500">
                                Joined: {formatDate(member.joinedAt)}
                              </span>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span>{member.user.email}</span>
                              </div>
                              {member.user.phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="h-4 w-4 text-gray-400" />
                                  <span>{member.user.phone}</span>
                                </div>
                              )}
                              {getCityFromAddress(member.user.address) && (
                                <div className="flex items-center gap-2">
                                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span>{getCityFromAddress(member.user.address)}</span>
                                </div>
                              )}
                              {getUserExperience(member.user) && (
                                <div className="flex items-center gap-2">
                                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                  </svg>
                                  <span>Experience: {getUserExperience(member.user)}</span>
                                </div>
                              )}
                              {getUserSkills(member.user).length > 0 && (
                                <div className="flex items-start gap-2">
                                  <svg className="h-4 w-4 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                  </svg>
                                  <div>
                                    <span className="font-medium">Skills:</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {getUserSkills(member.user).map((skill, index) => (
                                        <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                          {skill}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="submission">
                    <div className="space-y-4">
                      {team.submission ? (
                        <div className="space-y-4">
                          <div className="border rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3">Project Submission</h4>
                            <div className="space-y-3">
                              <div>
                                <p><strong>Title:</strong> {team.submission.title}</p>
                                <p><strong>Submitted:</strong> {formatDate(team.submission.submittedAt)}</p>
                              </div>
                              <div>
                                <p><strong>Description:</strong></p>
                                <p className="text-gray-700">{team.submission.description}</p>
                              </div>
                              
                              {team.submission.githubLink && (
                                <div>
                                  <strong>GitHub:</strong>
                                  <a 
                                    href={team.submission.githubLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 ml-2"
                                  >
                                    View Repository
                                  </a>
                                </div>
                              )}
                              
                              {team.submission.demoLink && (
                                <div>
                                  <strong>Demo:</strong>
                                  <a 
                                    href={team.submission.demoLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 ml-2"
                                  >
                                    View Demo
                                  </a>
                                </div>
                              )}
                              
                              {team.submission.presentationLink && (
                                <div>
                                  <strong>Presentation:</strong>
                                  <a 
                                    href={team.submission.presentationLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 ml-2"
                                  >
                                    View Presentation
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 mb-2">No submission yet</h4>
                          <p className="text-gray-600">
                            The team hasn't submitted their project yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentorTeams; 